import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Zap,
  ArrowLeft,
  Search,
  MapPin,
  Building2,
  ExternalLink,
  Bookmark,
  BookmarkCheck,
  Loader2,
  Globe,
  Linkedin,
  Calendar
} from "lucide-react";

const ExternalJobs = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [keywords, setKeywords] = useState("");
  const [location, setLocation] = useState("");
  const [jobs, setJobs] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchUrl, setSearchUrl] = useState("");
  const [selectedSources, setSelectedSources] = useState(["linkedin"]);
  const [sourcesStatus, setSourcesStatus] = useState({});

  const sources = [
    { id: "linkedin", name: "LinkedIn", color: "bg-blue-600" }
  ];

  const toggleSource = (sourceId) => {
    setSelectedSources(prev => 
      prev.includes(sourceId)
        ? prev.filter(s => s !== sourceId)
        : [...prev, sourceId]
    );
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!keywords.trim()) {
      toast.error("Please enter job keywords");
      return;
    }

    if (selectedSources.length === 0) {
      toast.error("Please select at least one job source");
      return;
    }

    setLoading(true);
    setSearched(true);

    try {
      const params = new URLSearchParams({
        keywords: keywords.trim(),
        location: location.trim(),
        limit: "20",
        sources: selectedSources.join(",")
      });

      const response = await fetch(`${API}/external-jobs/aggregated?${params}`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
        setSearchUrl(data.search_url || "");
        setSourcesStatus(data.sources || {});
        if (data.jobs?.length === 0) {
          toast.info("No jobs found. Try different keywords.");
        } else {
          toast.success(`Found ${data.jobs.length} jobs from ${Object.keys(data.sources).length} sources`);
        }
      } else {
        toast.error("Failed to search jobs");
      }
    } catch (error) {
      toast.error("Search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveJob = async (job) => {
    try {
      const response = await fetch(`${API}/external-jobs/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(job),
      });

      if (response.ok) {
        setSavedJobs([...savedJobs, job.id]);
        toast.success("Job saved!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save job");
      }
    } catch (error) {
      toast.error("Failed to save job");
    }
  };

  const dashboardUrl = user?.role === "recruiter" ? "/dashboard/recruiter" : "/dashboard/jobseeker";

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen p-4 sm:p-6 pt-20 lg:pt-8">
        <div className="max-w-5xl mx-auto">
          {/* Page Header */}
          <div className="mb-6 sm:mb-8">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
              EXTERNAL <span className="text-crimson">JOBS</span>
            </h1>
            <p className="text-gray-400 text-sm sm:text-base">Search jobs across multiple job boards</p>
          </div>

          {/* Search Section */}
          <motion.div
            className="bg-obsidian-light border border-[#222] p-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >

            {/* Source Selection */}
            <div className="mb-4">
              <p className="text-sm text-gray-400 mb-2">Select job boards to search:</p>
              <div className="flex flex-wrap gap-2">
                {sources.map((source) => (
                  <button
                    key={source.id}
                    type="button"
                    onClick={() => toggleSource(source.id)}
                    className={`px-4 py-2 text-sm transition-all ${
                      selectedSources.includes(source.id)
                        ? `${source.color} text-white`
                        : "bg-obsidian border border-[#333] text-gray-400 hover:border-[#444]"
                    }`}
                    data-testid={`source-${source.id}`}
                  >
                    {source.name}
                  </button>
                ))}
              </div>
            </div>

            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Job title, keywords, or company"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  className="bg-obsidian border-[#333] text-white placeholder:text-gray-500"
                  data-testid="keywords-input"
                />
              </div>
              <div className="flex-1">
                <Input
                  placeholder="Location (city, state, or remote)"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="bg-obsidian border-[#333] text-white placeholder:text-gray-500"
                  data-testid="location-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || selectedSources.length === 0}
                className="bg-crimson hover:bg-crimson-hover text-white uppercase tracking-wider"
                data-testid="search-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </>
                )}
              </Button>
            </form>

            {/* Source Status */}
            {Object.keys(sourcesStatus).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {Object.entries(sourcesStatus).map(([source, status]) => (
                  <span
                    key={source}
                    className={`px-2 py-1 text-xs ${
                      status.status === "ok" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
                    }`}
                  >
                    {source}: {status.count || 0} jobs
                  </span>
                ))}
              </div>
            )}
          </motion.div>

          {/* Results */}
          {searched && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-bold">
                  {jobs.length} Job{jobs.length !== 1 ? "s" : ""} Found
                </h3>
                {searchUrl && (
                  <a
                    href={searchUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-crimson hover:underline flex items-center gap-1"
                  >
                    View on LinkedIn <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-crimson animate-spin" />
                </div>
              ) : jobs.length === 0 ? (
                <div className="bg-obsidian-light border border-[#222] p-8 text-center">
                  <Globe className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">No jobs found. Try different keywords.</p>
                  {searchUrl && (
                    <a
                      href={searchUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-2 text-crimson hover:underline"
                    >
                      Search directly on LinkedIn <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {jobs.map((job) => (
                    <motion.div
                      key={job.id}
                      className="bg-obsidian-light border border-[#222] p-6 hover:border-crimson/30 transition-all"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {job.source === "LinkedIn" && (
                              <Linkedin className="w-4 h-4 text-[#0077B5]" />
                            )}
                            <span className="text-xs text-gray-500 uppercase tracking-wider">
                              {job.source}
                            </span>
                          </div>
                          <h4 className="font-heading text-lg font-bold text-white mb-1">
                            {job.title}
                          </h4>
                          <div className="flex items-center gap-4 text-gray-400 text-sm mb-3">
                            <span className="flex items-center gap-1">
                              <Building2 className="w-4 h-4" />
                              {job.company}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {job.location}
                            </span>
                            {job.posted_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {new Date(job.posted_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          {job._note && (
                            <p className="text-yellow-500/80 text-sm italic">{job._note}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleSaveJob(job)}
                            disabled={savedJobs.includes(job.id)}
                            className={`p-2 rounded transition-colors ${
                              savedJobs.includes(job.id)
                                ? "text-crimson bg-crimson/10"
                                : "text-gray-400 hover:text-crimson hover:bg-crimson/10"
                            }`}
                            data-testid={`save-job-${job.id}`}
                          >
                            {savedJobs.includes(job.id) ? (
                              <BookmarkCheck className="w-5 h-5" />
                            ) : (
                              <Bookmark className="w-5 h-5" />
                            )}
                          </button>
                          {job.url && (
                            <a
                              href={job.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-gray-400 hover:text-white transition-colors"
                            >
                              <ExternalLink className="w-5 h-5" />
                            </a>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Info Section */}
          {!searched && (
            <motion.div
              className="bg-obsidian-light border border-[#222] p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="font-heading text-lg font-bold mb-4">About External Jobs</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-400">
                <div>
                  <h4 className="font-medium text-white mb-2">How it works</h4>
                  <p className="text-sm">
                    We search public job listings from LinkedIn to help you find opportunities 
                    beyond our platform. Save jobs you're interested in and apply directly on 
                    the job board.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-white mb-2">Limitations</h4>
                  <p className="text-sm">
                    Results depend on LinkedIn's public accessibility. Some searches may be 
                    limited. For best results, search directly on LinkedIn using the provided link.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ExternalJobs;
